import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { User } from 'src/app/models/user.model';
import { Order, OrderProduct } from 'src/app/models/order.model';
import { Address } from 'src/app/models/address.model';
import { Product } from 'src/app/models/product.model';
import { UserService } from 'src/app/services/user/user.service';
import { isNullOrUndefined } from 'util';

@Injectable({
  providedIn: 'root'
})
export class ShopService {

  category: string;
  order: Order;
  cntrProducts: BehaviorSubject<number>;

  lstProducts: Product[];

  URL = "https://ncvznkh234.execute-api.us-east-1.amazonaws.com/v1";
  private readonly STATIC_PRODUCTS_URL = "https://s3.amazonaws.com/misalonvirtual.org/catalogos/web/products.json";
  private readonly PRODUCTS_CACHE_KEY = "mvs-products-daily-v1";
  private readonly PRODUCTS_CACHE_TTL = 24 * 60 * 60 * 1000;
  private readonly CART_STORAGE_KEY = "mvs-shopping-cart-morada-v1";
  private productsRequest$: Observable<any>;

  constructor(
    private http: HttpClient,
    private userService: UserService,
  ) {
    this.initializeOrder();
    this.restoreCart();
  }

  public cleanShopService() {
    this.initializeOrder();
    this.removeStoredCart();
  }

  private initializeOrder() {
    this.cntrProducts = new BehaviorSubject(0);
    this.order = new Order();
    this.order.iva = 0;
    this.order.deliveryTimeRange = "";
    this.order.lstProducts = new Array<OrderProduct>();
    this.order.dateCreated = new Date();
    this.order.address = new Address();
  }

  private restoreCart() {
    try {
      const rawCart = localStorage.getItem(this.CART_STORAGE_KEY);
      if (!rawCart) {
        return;
      }
      const storedProducts = JSON.parse(rawCart);
      if (!Array.isArray(storedProducts)) {
        this.removeStoredCart();
        return;
      }
      this.order.lstProducts = storedProducts.filter(product =>
        product && product.sku && typeof product.name === "string"
      );
      this.cntrProducts.next(this.order.lstProducts.length);
      this.calculateSubtotal();
    } catch (_error) {
      this.removeStoredCart();
    }
  }

  private persistCart() {
    try {
      if (this.order.lstProducts.length === 0) {
        this.removeStoredCart();
        return;
      }
      localStorage.setItem(this.CART_STORAGE_KEY, JSON.stringify(this.order.lstProducts));
    } catch (_error) {
      // El carrito sigue disponible en memoria si el navegador bloquea localStorage.
    }
  }

  private removeStoredCart() {
    try {
      localStorage.removeItem(this.CART_STORAGE_KEY);
    } catch (_error) {
      // No se requiere ninguna acción adicional si localStorage no está disponible.
    }
  }

  public getProducts(): Observable<any> {
    if (this.lstProducts) {
      return of({ success: true, lstProducts: this.lstProducts });
    }

    const cached = this.readProductsCache();
    if (cached) {
      this.lstProducts = cached.lstProducts;
      return of(cached);
    }

    if (!this.productsRequest$) {
      this.productsRequest$ = this.http.get(this.STATIC_PRODUCTS_URL).pipe(
        catchError(() => this.http.get(this.URL + "/product/getListProduct")),
        tap((results: any) => {
          if (results && results.success && results.lstProducts) {
            results.lstProducts = this.normalizeProducts(results.lstProducts);
            this.lstProducts = results.lstProducts;
            this.writeProductsCache(results);
          }
        }),
        shareReplay(1)
      );
    }

    return this.productsRequest$;
  }

  public getProduct(id: string): Observable<any> {
    return this.getProducts().pipe(
      map((results: any) => {
        const product = (results.lstProducts || []).find((_product: Product) => String(_product.sku) === String(id));
        return product ? { success: true, product: product } : null;
      }),
      catchError(() => of(null)),
      map((result: any) => result || { success: false, product: null })
    );
  }

  private readProductsCache(): any {
    try {
      const raw = localStorage.getItem(this.PRODUCTS_CACHE_KEY);
      if (!raw) {
        return null;
      }
      const cached = JSON.parse(raw);
      if (!cached.savedAt || Date.now() - cached.savedAt > this.PRODUCTS_CACHE_TTL || !cached.lstProducts) {
        localStorage.removeItem(this.PRODUCTS_CACHE_KEY);
        return null;
      }
      return {
        success: true,
        generatedAt: cached.generatedAt,
        lstProducts: this.normalizeProducts(cached.lstProducts)
      };
    } catch (_error) {
      return null;
    }
  }

  private writeProductsCache(results: any) {
    try {
      localStorage.setItem(this.PRODUCTS_CACHE_KEY, JSON.stringify({
        savedAt: Date.now(),
        generatedAt: results.generatedAt,
        lstProducts: results.lstProducts,
      }));
    } catch (_error) {
      // El catálogo sigue funcionando en memoria si el navegador no permite almacenamiento local.
    }
  }

  private normalizeProducts(products: any[]): Product[] {
    return (products || []).map((product: any) => ({
      ...product,
      category: product.category ||
        (product.genericCategoryDTO && (product.genericCategoryDTO.$id || product.genericCategoryDTO.id)) ||
        ""
    }));
  }

  public getCartItemCount() {
    return this.cntrProducts;
  }


  public addToCar(_product) {
    var newProduct = _product as OrderProduct;
    let exist: number = this.order.lstProducts.filter(product => product.sku == newProduct.sku).length;
    if (exist > 0) {
      let position: number = this.order.lstProducts.findIndex(product => product.sku == newProduct.sku);
      this.order.lstProducts[position].quantity++;
    } else {
      newProduct.quantity = 1;
      this.order.lstProducts.push(newProduct);
      this.cntrProducts.next(this.cntrProducts.value + 1);
    }

    this.calculateSubtotal();
  }

  public decreaseProduct(product: OrderProduct) {
    this.calculateSubtotal();
  }

  public removeProduct(product: OrderProduct) {
    var index: number = 0;
    this.order.lstProducts.forEach(p => {
      if (p.sku == product.sku) {
        this.order.lstProducts.splice(index, 1);
        this.cntrProducts.next(this.cntrProducts.value - 1);
      }
      index++;
    });
      this.calculateSubtotal();
  }

  public calculateSubtotal() {

    let total = 0;
    let subtotal = 0;
    let discount = 0;
    this.order.lstProducts.map(
      product => {
        subtotal += product.price;
      }
    );

    if (this.hasCampaignDiscount()) {
      discount = Math.round(subtotal * 10) / 100;
    } else if (subtotal >= 400) {
      if (subtotal == 400) {
        discount = 20;
      } else if (subtotal > 400 && subtotal <= 600) {
        discount = 40;
      } else if (subtotal > 600 && subtotal <= 800) {
        discount = 60;
      } else if (subtotal > 800 && subtotal <= 1000) {
        discount = 80;
      } else if (subtotal > 1000 && subtotal <= 1200) {
        discount = 100;
      } else if (subtotal > 1200) {
        discount = 150;
      }
    }

    this.order.discount = discount
    this.order.subtotal = subtotal
    this.order.total = subtotal - discount
    this.persistCart();
  }

  public hasCampaignDiscount(): boolean {
    sessionStorage.removeItem('mvs_campaign_discount');
    return false;
  }

  public updateStatusOrder(orderNumber: string): Observable<any> {
    var json_object = {
      "orderNumber": orderNumber,
      "statusCode": "6035bdd58af75121c895939a"
    }
    return this.http.put(this.URL + "/product/updateStatusOrder", json_object);
  }

  public sendOrder(user: User, order: Order, typePay: string): Observable<any> {
    var address = order.address.street + " " + this.order.address.outdoorExt;
    if (order.address.outdoorInt) {
      address += ", int: " + order.address.outdoorInt;
    } else {
      address += ", int: s/n ";
    }

    address += order.address.suburb + ", " + order.address.townHall + ", " + order.address.postalCode + ", " + order.address.city;


    var json_object = {
      "idUser": user.id,
      "idClient": user.client,
      "address": address,
      "cp": order.address.postalCode,
      "total": order.total,
      "discount": order.discount,
      "iva": order.iva,
      "deliveryTime": order.deliveryTimeRange,
      "shippingCost": order.shippingcost,
      "subtotal": order.subtotal,
      "lstProduct": order.lstProducts,
      "status": "5d712d0e2387002350e41911",
      "typePay": typePay,
      "deliveryNote": order.deliveryNote,
      "personal1": order.address.personal,
      "personal2": order.address.personal2,
      "phone1": order.address.phone,
      "mobile1": order.address.cellphone
    }

    return this.http.post(this.URL + "/product/sendOrder", json_object);
  }

  public inOrder(sku: string, lstProducts: Array<OrderProduct>): boolean {
    var inOrder = false;
    if (!isNullOrUndefined(lstProducts)) {
      const exist: number = lstProducts.filter(orderProduct => sku == orderProduct.sku).length;
      if (exist > 0) {
        inOrder = true
      }
    }
    return inOrder;
  }

}
